<template>
  <div class="min-h-screen">
    <AppNavbar title="Payments" :back="true" />

    <!-- ── Payment confirmation modal ── -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      style="background: rgba(0,0,0,0.45);"
      @click.self="closeModal"
    >
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

        <!-- Modal header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 class="text-base font-bold text-slate-800">Payment Details</h3>
          <button @click="closeModal" class="text-slate-400 hover:text-slate-600 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Modal body -->
        <div class="px-6 py-5">

          <!-- Loading preview -->
          <div v-if="previewLoading" class="flex flex-col items-center py-8">
            <svg class="w-6 h-6 animate-spin text-sky-500 mb-3" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            <p class="text-sm text-slate-500">Calculating fare…</p>
          </div>

          <!-- Preview error -->
          <div v-else-if="previewError" class="py-6 text-center">
            <div class="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-3">
              <svg class="w-6 h-6 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
              </svg>
            </div>
            <p class="text-sm font-semibold text-red-600 mb-2">{{ previewError }}</p>
            <button @click="loadPreview" class="text-xs text-sky-600 hover:underline">Retry</button>
          </div>

          <!-- Preview loaded -->
          <template v-else-if="preview">

            <!-- Route summary -->
            <div class="mb-4 p-4 bg-slate-50 rounded-xl">
              <div class="flex items-center gap-2.5 mb-2">
                <span class="w-2.5 h-2.5 bg-blue-500 rounded-full flex-shrink-0"></span>
                <span class="text-sm font-semibold text-slate-700 leading-snug">{{ preview.startingLocation }}</span>
              </div>
              <div class="ml-[4px] w-px h-3 bg-slate-300 my-1"></div>
              <div class="flex items-center gap-2.5">
                <span class="w-2.5 h-2.5 bg-emerald-500 rounded-full flex-shrink-0"></span>
                <span class="text-sm font-semibold text-slate-700 leading-snug">{{ preview.endingLocation }}</span>
              </div>
            </div>

            <!-- Booking meta -->
            <div class="grid grid-cols-2 gap-x-4 gap-y-3 text-xs mb-4">
              <div>
                <p class="text-slate-400 uppercase tracking-wide font-bold mb-0.5">Date &amp; time</p>
                <p class="text-slate-700 font-medium">{{ formatDate(preview.bookingDateTime) }}</p>
              </div>
              <div>
                <p class="text-slate-400 uppercase tracking-wide font-bold mb-0.5">Cab type</p>
                <p class="text-slate-700 font-medium">{{ preview.cabType }}</p>
              </div>
              <div>
                <p class="text-slate-400 uppercase tracking-wide font-bold mb-0.5">Passengers</p>
                <p class="text-slate-700 font-medium">{{ preview.numberOfPassengers }}</p>
              </div>
              <div>
                <p class="text-slate-400 uppercase tracking-wide font-bold mb-0.5">Currency</p>
                <p class="text-slate-700 font-medium">{{ preview.currency }}</p>
              </div>
            </div>

            <!-- Fare breakdown -->
            <div class="border border-slate-100 rounded-xl p-4 mb-4">
              <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Fare breakdown</p>
              <div class="space-y-2 text-xs">
                <div class="flex justify-between">
                  <span class="text-slate-500">Cab fare</span>
                  <span class="font-semibold text-slate-700">{{ preview.currency }} {{ Number(preview.cabFare).toFixed(2) }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-slate-500">Cab multiplier</span>
                  <span class="font-semibold text-slate-700">× {{ preview.cabMultiplier }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-slate-500">Daytime multiplier</span>
                  <span class="font-semibold text-slate-700">× {{ preview.daytimeMultiplier }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-slate-500">Passengers multiplier</span>
                  <span class="font-semibold text-slate-700">× {{ preview.passengersMultiplier }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-slate-500">Discount multiplier</span>
                  <span class="font-semibold text-slate-700">× {{ preview.discountMultiplier }}</span>
                </div>
              </div>
            </div>

            <!-- Total -->
            <div class="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 mb-5 flex items-center justify-between">
              <span class="text-sm font-bold text-emerald-700">Total to pay</span>
              <span class="text-2xl font-bold text-emerald-700">{{ preview.currency }} {{ Number(preview.totalPrice).toFixed(2) }}</span>
            </div>

            <!-- Payment method selector -->
            <div class="mb-4">
              <p class="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Payment method</p>
              <div class="grid grid-cols-4 gap-2">
                <button
                  v-for="m in paymentMethods"
                  :key="m.value"
                  type="button"
                  @click="paymentMethod = m.value"
                  :class="[
                    'flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl border text-xs font-semibold transition-colors',
                    paymentMethod === m.value
                      ? 'bg-blue-50 border-blue-400 text-blue-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                  ]"
                >
                  <span class="text-lg leading-none">{{ m.icon }}</span>
                  <span>{{ m.label }}</span>
                </button>
              </div>
            </div>

            <!-- Card fields (UI only — not sent to backend) -->
            <div v-if="paymentMethod === 'CARD'" class="space-y-3 mb-2">
              <div>
                <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Cardholder name</label>
                <input
                  v-model="cardName"
                  type="text"
                  placeholder="Name on card"
                  class="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm bg-slate-50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
              </div>
              <div>
                <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Card number</label>
                <input
                  v-model="cardNumber"
                  type="text"
                  placeholder="•••• •••• •••• ••••"
                  maxlength="19"
                  class="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm bg-slate-50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition font-mono tracking-widest"
                />
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Expiry</label>
                  <input
                    v-model="cardExpiry"
                    type="text"
                    placeholder="MM / YY"
                    maxlength="7"
                    class="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm bg-slate-50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                </div>
                <div>
                  <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">CVV</label>
                  <input
                    v-model="cardCvv"
                    type="text"
                    placeholder="•••"
                    maxlength="4"
                    class="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm bg-slate-50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                </div>
              </div>
            </div>

          </template>
        </div>

        <!-- Modal footer -->
        <div class="flex gap-3 px-6 pb-6">
          <button
            @click="closeModal"
            class="flex-1 border border-slate-200 text-slate-600 hover:bg-slate-50 py-2.5 rounded-xl text-sm font-semibold transition-colors"
          >
            Cancel
          </button>
          <button
            @click="confirmPayment"
            :disabled="!preview || previewLoading || !!confirmingId"
            class="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <svg v-if="confirmingId" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            {{ confirmingId ? 'Processing…' : 'Confirm payment' }}
          </button>
        </div>

      </div>
    </div>
    <!-- ── /modal ── -->

    <main class="max-w-7xl mx-auto px-8 py-8">

      <!-- Page header -->
      <div class="mb-8">
        <div class="flex items-center gap-2 text-sm text-slate-400 mb-3">
          <span>Dashboard</span>
          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
          </svg>
          <span class="text-slate-600 font-medium">Payments</span>
        </div>
        <div class="flex items-start justify-between gap-6">
          <div>
            <h1 class="text-3xl font-bold text-slate-800">Payments</h1>
            <p class="text-slate-500 mt-1.5 text-base">Pay for outstanding bookings or review your payment history.</p>
          </div>
          <button
            @click="loadBookings"
            class="flex-shrink-0 flex items-center gap-2 bg-white border border-slate-200 text-slate-600 hover:text-sky-600 hover:border-sky-200 hover:bg-sky-50 px-4 py-2 rounded-xl text-sm font-medium transition-colors shadow-sm"
          >
            <svg class="w-4 h-4" :class="{ 'animate-spin': loading }" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            Refresh
          </button>
        </div>
        <div class="mt-4 space-y-2">
          <AlertBanner :message="message" type="success" />
          <AlertBanner :message="error" type="error" />
        </div>
      </div>

      <!-- Full-page loading -->
      <div v-if="loading" class="bg-white border border-slate-100 rounded-2xl shadow-sm p-12 flex flex-col items-center justify-center">
        <svg class="w-6 h-6 animate-spin text-sky-500 mb-3" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
        </svg>
        <p class="text-sm text-slate-500">Loading bookings…</p>
      </div>

      <!-- Content -->
      <div v-else class="space-y-10">

        <!-- ── Unpaid section ── -->
        <section>
          <div class="flex items-center gap-2.5 mb-4">
            <div class="w-2 h-2 rounded-full bg-red-400"></div>
            <h2 class="text-base font-bold text-slate-800">Unpaid</h2>
            <span
              v-if="unpaidBookings.length"
              class="text-xs font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full"
            >{{ unpaidBookings.length }}</span>
          </div>

          <!-- Empty -->
          <div v-if="unpaidBookings.length === 0" class="bg-white border border-slate-100 rounded-2xl shadow-sm p-10 flex flex-col items-center text-center">
            <div class="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mb-3">
              <svg class="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <p class="text-sm font-semibold text-slate-700 mb-1">All paid up</p>
            <p class="text-xs text-slate-400">No outstanding payments. Great work!</p>
          </div>

          <!-- List -->
          <div v-else class="space-y-3">
            <div
              v-for="booking in unpaidBookings"
              :key="booking._id"
              class="bg-white border border-red-100 rounded-2xl shadow-sm p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
            >
              <!-- Left: route + meta -->
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2 mb-1.5 min-w-0">
                  <span class="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                  <span class="text-sm font-semibold text-slate-800 truncate">{{ booking.startingLocation }}</span>
                  <svg class="w-3.5 h-3.5 text-slate-300 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                  </svg>
                  <span class="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0"></span>
                  <span class="text-sm font-semibold text-slate-800 truncate">{{ booking.endingLocation }}</span>
                </div>
                <div class="flex items-center gap-2 flex-wrap text-xs text-slate-500 mb-2">
                  <span>{{ formatDate(booking.bookingDateTime) }}</span>
                  <span class="text-slate-200">·</span>
                  <span>{{ booking.cabType }}</span>
                  <span class="text-slate-200">·</span>
                  <span>{{ booking.numberOfPassengers }} passengers</span>
                </div>
                <span class="text-xs font-semibold bg-red-50 text-red-600 px-2 py-0.5 rounded-full">{{ booking.paymentStatus }}</span>
              </div>

              <!-- Right: view details button -->
              <button
                @click="openModal(booking)"
                class="flex-shrink-0 flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                </svg>
                View &amp; Pay
              </button>
            </div>
          </div>
        </section>

        <!-- ── Paid section ── -->
        <section>
          <div class="flex items-center gap-2.5 mb-4">
            <div class="w-2 h-2 rounded-full bg-emerald-400"></div>
            <h2 class="text-base font-bold text-slate-800">Paid</h2>
            <span
              v-if="paidBookings.length"
              class="text-xs font-bold bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-full"
            >{{ paidBookings.length }}</span>
          </div>

          <!-- Empty -->
          <div v-if="paidBookings.length === 0" class="bg-white border border-slate-100 rounded-2xl shadow-sm p-10 flex flex-col items-center text-center">
            <div class="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center mb-3">
              <svg class="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
              </svg>
            </div>
            <p class="text-sm font-semibold text-slate-700 mb-1">No paid bookings yet</p>
            <p class="text-xs text-slate-400">Completed payments will appear here.</p>
          </div>

          <!-- Grid -->
          <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div
              v-for="booking in paidBookings"
              :key="booking._id"
              class="bg-white border border-slate-100 rounded-2xl shadow-sm p-5"
            >
              <div class="flex items-center gap-2 mb-1.5 min-w-0">
                <span class="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                <span class="text-sm font-semibold text-slate-800 truncate">{{ booking.startingLocation }}</span>
                <svg class="w-3.5 h-3.5 text-slate-300 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                </svg>
                <span class="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0"></span>
                <span class="text-sm font-semibold text-slate-800 truncate">{{ booking.endingLocation }}</span>
              </div>
              <div class="flex items-center gap-2 flex-wrap text-xs text-slate-500 mb-3">
                <span>{{ formatDate(booking.bookingDateTime) }}</span>
                <span class="text-slate-200">·</span>
                <span>{{ booking.cabType }}</span>
                <span class="text-slate-200">·</span>
                <span>{{ booking.numberOfPassengers }} passengers</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-xs font-semibold bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                  </svg>
                  Paid
                </span>
                <span :class="['text-xs font-semibold px-2 py-0.5 rounded-full', statusClass(booking.status)]">
                  {{ booking.status }}
                </span>
              </div>
            </div>
          </div>
        </section>

      </div>
    </main>
  </div>
</template>

<script>
import AppNavbar from "@/components/AppNavbar.vue";
import AlertBanner from "@/components/AlertBanner.vue";

const statusColors = {
  PENDING:   'bg-amber-50 text-amber-700',
  CONFIRMED: 'bg-blue-50 text-blue-700',
  COMPLETED: 'bg-emerald-50 text-emerald-700',
  CANCELLED: 'bg-red-50 text-red-700',
}

export default {
  name: "PaymentsView",
  components: { AppNavbar, AlertBanner },
  data() {
    return {
      bookings: [],
      message: "",
      error: "",
      loading: false,

      // Modal state
      showModal: false,
      selectedBooking: null,
      preview: null,
      previewLoading: false,
      previewError: "",
      confirmingId: null,

      // Payment method
      paymentMethod: "CARD",
      paymentMethods: [
        { value: "CARD",    label: "Card",    icon: "💳" },
        { value: "CASH",    label: "Cash",    icon: "💵" },
        { value: "REVOLUT", label: "Revolut", icon: "🔵" },
        { value: "PAYPAL",  label: "PayPal",  icon: "🅿️" },
      ],

      // Card UI fields (not sent to backend)
      cardName:   "",
      cardNumber: "",
      cardExpiry: "",
      cardCvv:    "",
    };
  },
  computed: {
    unpaidBookings() {
      return this.bookings.filter(b => b.paymentStatus !== 'PAID');
    },
    paidBookings() {
      return this.bookings.filter(b => b.paymentStatus === 'PAID');
    },
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push("/login");
      return;
    }
    this.loadBookings();
  },
  methods: {
    async loadBookings() {
      try {
        this.loading = true;
        this.error = "";
        const response = await this.$store.dispatch("fetchBookings");
        this.bookings = response.data.bookings;
      } catch (error) {
        this.error = error.response?.data?.message || "Could not load bookings";
      } finally {
        this.loading = false;
      }
    },

    openModal(booking) {
      this.selectedBooking = booking;
      this.preview = null;
      this.previewError = "";
      this.paymentMethod = "CARD";
      this.cardName = "";
      this.cardNumber = "";
      this.cardExpiry = "";
      this.cardCvv = "";
      this.showModal = true;
      this.loadPreview();
    },

    closeModal() {
      this.showModal = false;
      this.selectedBooking = null;
      this.preview = null;
      this.previewError = "";
      this.confirmingId = null;
    },

    async loadPreview() {
      if (!this.selectedBooking) return;
      try {
        this.previewLoading = true;
        this.previewError = "";
        const response = await this.$store.dispatch("previewPayment", {
          bookingId: this.selectedBooking._id,
          useDiscount: false,
        });
        this.preview = response.data.preview;
      } catch (error) {
        this.previewError = error.response?.data?.message || "Could not calculate payment preview.";
      } finally {
        this.previewLoading = false;
      }
    },

    async confirmPayment() {
      if (!this.selectedBooking || !this.preview) return;
      try {
        this.message = "";
        this.error = "";
        this.confirmingId = this.selectedBooking._id;

        await this.$store.dispatch("payBooking", {
          bookingId: this.selectedBooking._id,
          paymentMethod: this.paymentMethod,
          useDiscount: false,
        });

        this.closeModal();
        this.message = "Payment completed successfully.";
        await this.loadBookings();
      } catch (error) {
        this.error = error.response?.data?.message || "Payment failed";
        this.confirmingId = null;
      }
    },

    formatDate(date) {
      return new Date(date).toLocaleString();
    },

    statusClass(status) {
      return statusColors[status] || 'bg-slate-100 text-slate-600';
    },
  },
};
</script>
